import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Grid, Paper, Button, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    ImageList, ImageListItem, ImageListItemBar, Chip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import { useSearchParams } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { useAccesos } from '../contexts/AccesosContext';
import Swal from 'sweetalert2';

function CapturasPantalla() {
    const { tienePermiso } = useAccesos();
    const { user } = useUser();
    const server = process.env.REACT_APP_API_ROUTE;
    const [searchParams] = useSearchParams();
    const informeId = searchParams.get('informeId');
    const estudioId = searchParams.get('estudioId');
    const detalleId = searchParams.get('detalleId');

    const [detalleInfo, setDetalleInfo] = useState(null);
    const [capturas, setCapturas] = useState([]);
    const [openPreview, setOpenPreview] = useState(false);
    const [capturaPreview, setCapturaPreview] = useState(null);
    const [loading, setLoading] = useState(false);

    const fetchDetalleInfo = async () => {
        if (!detalleId || detalleId === 'undefined') return;
        try {
            const response = await fetch(`${server}/MgOrdenesDeTrabajoDetalles/imagenes/detalle-completo/${detalleId}`);
            if (response.ok) {
                const data = await response.json();
                setDetalleInfo(data);
            }
        } catch (error) {
            console.error('Error al cargar detalle:', error);
        }
    };

    const fetchCapturas = async () => {
        if (!informeId && !estudioId) return;
        try {
            const endpoint = informeId
                ? `${server}/ImgCapturasPantalla/porinforme/${informeId}`
                : `${server}/ImgCapturasPantalla/porestudio/${estudioId}`;
            const response = await fetch(endpoint);
            const data = await response.json();
            setCapturas(data.$values || data || []);
        } catch (error) {
            console.error('Error al cargar capturas:', error);
        }
    };

    useEffect(() => {
        fetchDetalleInfo();
        fetchCapturas();
    }, [informeId, estudioId]);

    const handleSubirCaptura = async (e) => {
        if (!tienePermiso('Subir Captura IMG')) return;
        const file = e.target.files[0];
        if (!file) return;

        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            if (informeId) formData.append('idInforme', informeId);
            if (estudioId) formData.append('idEstudio', estudioId);
            formData.append('descripcion', file.name);

            const response = await fetch(`${server}/ImgCapturasPantalla/upload`, {
                method: 'POST',
                body: formData
            });

            if (response.ok) {
                fetchCapturas();
                Swal.fire('Éxito', 'Captura subida correctamente', 'success');
            } else {
                Swal.fire('Error', 'Error al subir captura', 'error');
            }
        } catch (error) {
            Swal.fire('Error', error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleEliminar = async (captura) => {
        if (!tienePermiso('Eliminar Captura IMG')) return;
        const result = await Swal.fire({
            title: '¿Eliminar captura?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Eliminar',
            cancelButtonText: 'Cancelar'
        });
        if (!result.isConfirmed) return;

        try {
            const response = await fetch(
                `${server}/ImgCapturasPantalla/${captura.idCaptura}/${user.codigoUsuario.padEnd(20)}/`,
                { method: 'DELETE' }
            );
            if (response.ok) {
                fetchCapturas();
                Swal.fire('Éxito', 'Captura eliminada', 'success');
            }
        } catch (error) {
            Swal.fire('Error', error.message, 'error');
        }
    };

    if (!informeId && !estudioId) {
        return (
            <div className="right_col" role="main">
            <div className="x_content">
                <div className="x_title"><h2>Capturas de Pantalla</h2></div>
                <Typography>Seleccione un informe o estudio para ver sus capturas.</Typography>
            </div>
            </div>
        );
    }

    return (
        <div className="right_col" role="main">
        <div className="x_content">
            <div className="x_title">
                <h2>Capturas de Pantalla
                    {informeId && ` — Informe #${informeId}`}
                    {estudioId && ` — Estudio #${estudioId}`}
                </h2>
            </div>

            {detalleInfo && (
                <Box sx={{ p: 1.5, bgcolor: '#e3f2fd', borderRadius: 1, mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Chip label={`Detalle #${detalleInfo.idDetalle}`} size="small" color="primary" />
                    <Typography variant="body2"><strong>Paciente:</strong> {detalleInfo.nombrePaciente}</Typography>
                    <Typography variant="body2"><strong>Examen:</strong> {detalleInfo.nombreProducto}</Typography>
                    <Chip label={detalleInfo.estadoFlujo || 'pendiente'} size="small" variant="outlined" />
                </Box>
            )}

            {/* Botón subir */}
            <Box sx={{ mb: 2 }}>
                <Button
                    variant="contained"
                    component="label"
                    startIcon={<CameraAltIcon />}
                    disabled={loading || !tienePermiso('Subir Captura IMG')}
                >
                    {loading ? 'Subiendo...' : 'Subir Captura'}
                    <input type="file" hidden accept="image/*" onChange={handleSubirCaptura} />
                </Button>
            </Box>

            {/* Galería */}
            {capturas.length > 0 ? (
                <ImageList cols={3} gap={8}>
                    {capturas.map((captura) => (
                        <ImageListItem key={captura.idCaptura}>
                            <img
                                src={captura.sasUrl || captura.urlImagen}
                                alt={captura.descripcion || 'Captura'}
                                loading="lazy"
                                style={{ cursor: 'pointer', borderRadius: 4 }}
                                onClick={() => { setCapturaPreview(captura); setOpenPreview(true); }}
                            />
                            <ImageListItemBar
                                title={captura.descripcion || 'Sin descripción'}
                                subtitle={captura.fechaCreacion ? new Date(captura.fechaCreacion).toLocaleString() : ''}
                                actionIcon={
                                    <Box>
                                        <IconButton
                                            sx={{ color: 'white' }}
                                            onClick={() => { setCapturaPreview(captura); setOpenPreview(true); }}
                                        >
                                            <FullscreenIcon />
                                        </IconButton>
                                        <IconButton
                                            sx={{ color: 'white' }}
                                            onClick={() => handleEliminar(captura)}
                                            disabled={!tienePermiso('Eliminar Captura IMG')}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Box>
                                }
                            />
                        </ImageListItem>
                    ))}
                </ImageList>
            ) : (
                <Typography color="textSecondary">No hay capturas disponibles.</Typography>
            )}

            {/* Preview Dialog */}
            <Dialog open={openPreview} onClose={() => setOpenPreview(false)} maxWidth="lg" fullWidth>
                <DialogTitle>{capturaPreview?.descripcion || 'Vista previa'}</DialogTitle>
                <DialogContent>
                    {capturaPreview && (
                        <img
                            src={capturaPreview.sasUrl || capturaPreview.urlImagen}
                            alt={capturaPreview.descripcion}
                            style={{ width: '100%', height: 'auto' }}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenPreview(false)}>Cerrar</Button>
                </DialogActions>
            </Dialog>
        </div>
        </div>
    );
}

export default CapturasPantalla;
